import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as styles from './styles';
import { BodyProps, TableCellProps, TableRowProps } from './models';
import { Row } from 'react-table';
import arrowUp from '../../../../../assets/arrow-up-blue.svg';
import arrowDown from '../../../../../assets/arrow-down.svg';
import { ActionsMenu } from '../../../actions-menu/actions-menu.component';
import { Icon } from '../../../../icon/Icon';
import { useOnScreen } from '../../../../../helpers/hooks';

const TableExpandableRow = styled.tr`
  ${styles.TableExpandableRow}
`;
const TableExpandableCell = styled.td`
  ${styles.TableExpandableCell}
`;
const TableSmallCell = styled.td`
  ${styles.TableSmallCell}
`;

const SIcon = styled(Icon)`
  width: 12px;
  height: 7.4px;
  border-radius: 50px;
  margin-right: 18px;
  align-self: center;
  display: flex;
  color: #002169;
`;

const CellSpan = styled.span`
  display: -webkit-box;
  min-width: 150px;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TableCell = memo(
  styled(({ className, isGrouped, isAggregated, cell, row }: TableCellProps) => {
    return (
      <td className={className}>
        {isGrouped ? (
          // If it's a grouped cell, add an expander and row count
          <span className="group-container">
            <span className="icon-container" {...row.getToggleRowExpandedProps()}>
              {row.isExpanded ? <SIcon src={arrowUp} /> : <SIcon src={arrowDown} />}
            </span>
            <CellSpan>{cell.render('Cell')}</CellSpan>
          </span>
        ) : isAggregated ? (
          // If the cell is aggregated, use the Aggregated
          // renderer for cell
          <CellSpan>{cell.render('Aggregated')}</CellSpan>
        ) : cell.isPlaceholder ? null : ( // For cells with repeated values, render null
          // Otherwise, just render the regular cell
          <CellSpan>{cell.render('Cell')}</CellSpan>
        )}
      </td>
    );
  })`
    ${styles.TableCell}
  `
);

const TableRow = memo(
  styled(({ className, renderExpandableTemplate, row, toggleRowExpand, actionsMenu, groupBy }: TableRowProps) => {
    return (
      <tr className={className}>
        {renderExpandableTemplate && (
          <TableSmallCell>
            <span className="cell-container">
              <span
                className="icon-container"
                {...row.getToggleRowExpandedProps()}
                onClick={() => toggleRowExpand(row)}
              >
                {row.isExpanded ? <SIcon src={arrowUp} /> : <SIcon src={arrowDown} />}
              </span>
            </span>
          </TableSmallCell>
        )}

        {row.cells.map((cell, index) => {
          const { key: cellKey, ...cellProps } = cell.getCellProps();
          return (
            <TableCell
              {...{
                key: cellKey,
                ...cellProps,
                cell,
                cellAmount: row.cells.length,
                cellIndex: index,
                isGrouped: row.isGrouped,
                isText: typeof cell.value === 'string',
                isExpanded: row.isExpanded,
                groupBy,
                isGroupedCell: cell.isGrouped,
              }}
            />
          );
        })}
        {actionsMenu && (
          <TableSmallCell>
            <ActionsMenu {...actionsMenu} rowData={row.original} />
          </TableSmallCell>
        )}
      </tr>
    );
  })`
    ${styles.TableRow}
  `
);

export const Body = styled(
  ({
    className,
    bodyProps,
    actionsMenu,
    rows,
    emptyState,
    groupBy,
    expanded,
    onCollapseRow,
    onExpandRow,
    emptyStateTemplate,
    prepareRow,
    columns,
    renderExpandableTemplate,
    lazyLoadingOptions,
  }: BodyProps) => {
    // Enable actions when reach the bottom of the table (such as lazy loading)
    const bottomListElementRef = useRef(null);
    const isBottomElementVisible = lazyLoadingOptions ? useOnScreen(bottomListElementRef) : false;

    useEffect(() => {
      if (isBottomElementVisible) {
        lazyLoadingOptions.onBottomReached();
      }
    }, [isBottomElementVisible]);

    const toggleRowExpand = useCallback(
      (currentRow: Row) => {
        if (expanded[currentRow.id]) {
          currentRow.toggleRowExpanded(false);
          onCollapseRow?.(currentRow);
        } else {
          rows.forEach((row) => {
            if (row.id !== currentRow.id && row.isExpanded) {
              row.toggleRowExpanded(false);
              onCollapseRow?.(row);
            }

            if (row.id === currentRow.id) {
              row.toggleRowExpanded(true);
            }
          });
          onExpandRow?.(currentRow);
        }
      },
      [expanded]
    );

    const hasActions = !!actionsMenu?.actionMenuItems?.length;
    const totalColSpans = (() => {
      let totalColSpans = columns.length;
      if (hasActions) totalColSpans++;

      if (renderExpandableTemplate) totalColSpans++;

      return totalColSpans;
    })();

    const tableContent = (() => {
      if (emptyState)
        return (
          <TableExpandableRow>
            <TableExpandableCell colSpan={totalColSpans}>{emptyStateTemplate}</TableExpandableCell>
          </TableExpandableRow>
        );

      return rows.map((row: Row, index) => {
        prepareRow(row);

        const { key: rowKey, ...rowProps } = row.getRowProps();

        // In case of lazy loading table, use placeholder to trigger next page fetch
        const shouldPlaceFetchMoreElement =
          lazyLoadingOptions &&
          index === rows.length - (lazyLoadingOptions.indexOffset || 8);

        return (
          <React.Fragment key={index}>
            <TableRow
              {...{
                key: rowKey,
                toggleRowExpand,
                renderExpandableTemplate,
                row,
                actionsMenu,
                groupBy,
                rowProps,
              }}
            />

            {renderExpandableTemplate && row.isExpanded && (
              <TableExpandableRow key={`expandable-row-${rowKey}`}>
                <TableExpandableCell key={`expandable-column-${rowKey}`} colSpan={totalColSpans}>
                  {renderExpandableTemplate()}
                </TableExpandableCell>
              </TableExpandableRow>
            )}
            {shouldPlaceFetchMoreElement && <div ref={bottomListElementRef} className="bottom-element" />}
          </React.Fragment>
        );
      });
    })();

    return (
      <tbody {...{ className, ...bodyProps }}>
        {tableContent}
        {lazyLoadingOptions && (
          <tr>
            <td align="center" colSpan={totalColSpans}>
              {lazyLoadingOptions.isLoading && lazyLoadingOptions.LoadingRowComponent}
            </td>
          </tr>
        )}
      </tbody>
    );
  }
)`
  ${styles.Body}
`;
